/**
 * useBreadcrumbs Composable — Donor Portal
 *
 * Generates breadcrumb items from the current route's `meta.breadcrumbs`
 * array, or auto-generates from the path when meta is absent.
 * Supports both plain-string and function-getter labels for i18n-aware routers.
 *
 * The computed depends on the reactive `locale` ref so breadcrumbs re-render
 * immediately when the user switches language.
 */
import { computed, type Component } from 'vue';
import { useRoute } from 'vue-router';
import { useLocale } from '@/shared/composables/useLocale';

export interface BreadcrumbItem {
  label: string | (() => string);
  to?: string;
  icon?: Component;
}

function resolveLabel(label: string | (() => string), t: (key: string) => string): string {
  if (typeof label === 'function') return label();
  return t(label);
}

export function useBreadcrumbs() {
  const route = useRoute();
  const { t, locale } = useLocale();

  const breadcrumbs = computed<{ label: string; to?: string; icon?: Component }[]>(() => {
    // Referencing locale.value ensures the computed re-evaluates on language switch
    void locale.value;
    const homeLabel = t('common.home');

    const items: { label: string; to?: string; icon?: Component }[] = [];

    if (route.meta.breadcrumbs) {
      // Route owns its full trail — use it as-is, no auto-prepend
      const metaCrumbs = route.meta.breadcrumbs as BreadcrumbItem[];
      items.push(
        ...metaCrumbs.map((b) => ({
          ...b,
          label: resolveLabel(b.label, t),
        })),
      );
    } else {
      // Auto-generate from path segments, always prepend Home
      items.push({ label: homeLabel, to: '/' });
      const segments = route.path.split('/').filter(Boolean);
      segments.forEach((segment, index) => {
        const path = '/' + segments.slice(0, index + 1).join('/');
        const isLast = index === segments.length - 1;
        const label =
          isLast && route.meta.title
            ? String(route.meta.title)
            : segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
        items.push({ label, ...(isLast ? {} : { to: path }) });
      });
    }

    return items;
  });

  return { breadcrumbs };
}
